/**
 * CaseAlem — живой кейс Café Әлем (первое кафе платформы).
 * Editorial-цитата serif italic + реальные цифры из Supabase
 * (count-up) + телефон со скриншотом живого меню + ссылка на витрину.
 *
 * Цифры сняты 2026-07-03 из Supabase (tenant alem): 54 блюда, 9 категорий,
 * 14 столов, 15 сотрудников, 6 блюд в AR, live с 2026-05-08.
 * Это ручной снимок (не live-fetch) — обновлять при заметном росте меню.
 */
import { ArrowUpRight, BadgeCheck } from "lucide-react";
import { useLang } from "../i18n";
import { useReveal, useCountUp } from "../lib/motion";
import { SectionHead } from "./SectionHead";
import { shotUrl } from "../content/apps";

const ALEM_URL = "https://alem.e-menu.bar/menu/";

export function CaseAlem() {
  const { t } = useLang();
  const ref = useReveal<HTMLElement>();

  return (
    <section
      id="case"
      ref={ref}
      className="relative overflow-hidden border-t"
      style={{ borderColor: "var(--line)", background: "var(--bg-deep)" }}
    >
      {/* Контурное фоновое слово */}
      <div
        className="t-serif-i t-ghost pointer-events-none absolute -right-6 top-12 select-none text-[clamp(5rem,14vw,13rem)] leading-none"
        aria-hidden
      >
        Әлем
      </div>

      <div
        className="relative mx-auto w-full max-w-7xl px-5 sm:px-8"
        style={{ paddingTop: "var(--space-section)", paddingBottom: "var(--space-section)" }}
      >
        <SectionHead
          num="05"
          label={t.caseAlem.label}
          title={t.caseAlem.title}
          accent={t.caseAlem.accent}
        />

        <div className="grid items-center gap-8 lg:grid-cols-[1.3fr_1fr] lg:gap-14">
          <div>
            <p
              className="t-serif-i reveal mb-8 text-3xl leading-snug sm:text-4xl"
              data-reveal=""
              style={{ color: "var(--fg)" }}
            >
              “{t.caseAlem.quote}”
            </p>

            <div className="reveal mb-12 flex items-center gap-4" data-reveal="">
              <span
                className="flex h-12 w-12 items-center justify-center rounded-full border t-brand text-sm"
                style={{ borderColor: "var(--gold-line)", color: "var(--gold)" }}
              >
                Ә
              </span>
              <div>
                <p className="font-bold" style={{ color: "var(--fg)" }}>
                  Café Әлем
                </p>
                <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
                  {t.caseAlem.location}
                </p>
              </div>
              <span
                className="ml-2 inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em]"
                style={{ borderColor: "var(--gold-line)", color: "var(--gold-bright)" }}
              >
                <BadgeCheck className="h-3.5 w-3.5" aria-hidden />
                {t.caseAlem.liveBadge}
              </span>
            </div>

            {/* Реальные цифры */}
            <div
              className="reveal grid grid-cols-2 gap-px sm:grid-cols-4"
              data-reveal=""
              style={{ background: "var(--line)" }}
            >
              <CaseStat value={54} label={t.caseAlem.stats.dishes} />
              <CaseStat value={14} label={t.caseAlem.stats.tables} />
              <CaseStat value={15} label={t.caseAlem.stats.staff} />
              <CaseStat value={6} label={t.caseAlem.stats.ar} />
            </div>

            <div className="reveal mt-10" data-reveal="">
              <a href={ALEM_URL} target="_blank" rel="noreferrer" className="btn-gold">
                {t.caseAlem.cta}
                <ArrowUpRight className="h-4 w-4" aria-hidden />
              </a>
              <p className="mt-4 text-sm" style={{ color: "var(--fg-muted)" }}>
                {t.caseAlem.ctaNote}
              </p>
            </div>
          </div>

          {/* Телефон со скриншотом живого меню */}
          <div
            className="reveal mx-auto w-[250px] sm:w-[280px]"
            data-reveal=""
            style={{ "--reveal-delay": "180ms" } as React.CSSProperties}
          >
            <div
              className="phone-frame"
              style={{ height: 560, transform: "rotate(2.5deg)" }}
            >
              <div
                className="absolute inset-0 flex items-center justify-center"
                style={{ background: "linear-gradient(160deg, #17140c 0%, #0a0908 70%)" }}
              >
                <span className="t-display text-xl" style={{ color: "var(--fg-soft)" }}>
                  alem.e-menu.bar
                </span>
              </div>
              <img
                src={shotUrl("alem-live.jpg")}
                alt="Живое меню Café Әлем"
                loading="lazy"
                className="relative"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = "none";
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CaseStat({ value, label }: { value: number; label: string }) {
  const { ref, display } = useCountUp(value);
  return (
    <div className="p-6" style={{ background: "var(--bg-deep)" }}>
      <span ref={ref} className="t-brand block text-4xl" style={{ color: "var(--gold)" }}>
        {display}
      </span>
      <span
        className="mt-2 block text-[11px] font-bold uppercase tracking-[0.16em]"
        style={{ color: "var(--fg-muted)" }}
      >
        {label}
      </span>
    </div>
  );
}
